/**
 * QUEM PODE O QUÊ — a matriz única de permissões da TELA (fase 3E, 06/10/2026).
 *
 * Por que existe: no segundo test drive o gestor entrou com o perfil Consulta,
 * montou uma pesagem inteira e só no «Sincronizar» ouviu «A Consulta não grava
 * fichas». O banco recusou certo; a TELA é que deixou fazer. Daqui em diante,
 * toda tela pergunta a `pode(papel, acao)` antes de mostrar um botão ou um
 * campo editável — e a resposta sai desta tabela, que ESPELHA as regras do
 * banco (políticas das migrações 001 a 013). Cada linha cita a regra que
 * espelha; `testes/permissoes.mjs` confere a matriz inteira e
 * `testes/banco.mjs` confere, ação por ação, que o banco diz o mesmo «não».
 *
 * A tela é cortesia, não segurança: a segurança continua no banco. Mas a
 * tela nunca pode oferecer o que o banco vai recusar.
 *
 * Decisões do gestor que a matriz guarda:
 *  - Consulta (secretária, esposa, escritório) VÊ TUDO e só DÁ BAIXA:
 *    lança e anula pagamento efetivado (quitação) e recebimento da revenda,
 *    com motivo, anexa os comprovantes deles e registra o pagamento do acerto
 *    (decisão de 06/10/2026, rodada 1 da revisão da fase 5). Não mexe em
 *    pesagem, negociação, comissão, equipe nem ferramentas;
 *  - comprador NÃO reescreve ficha encerrada: para mudar, reabre com motivo
 *    (013 fichas_guarda). A tela passa `encerrada: true` no contexto;
 *  - comprador grava só o que é dele: as fichas e os produtores que ele
 *    criou, as despesas do próprio extrato, as notas que ele registrou;
 *  - gestor grava tudo da empresa.
 *
 * Sem tela aqui: só a regra (src/dominio não importa nada de tela).
 */

export type Papel = 'gestor' | 'comprador' | 'consulta'

/**
 * - 'sim': pode, em tudo da empresa;
 * - 'seu': só no que é dele (ver `Contexto.seu`);
 * - 'nao': não pode.
 */
export type Regra = 'sim' | 'seu' | 'nao'

export type Acao =
  // ficha (tabela fichas: o documento inteiro mora num jsonb)
  | 'criarFicha' | 'editarOrigem' | 'editarNegociacao' | 'pesar' | 'encerrar' | 'reabrir'
  | 'cancelarFicha' | 'excluirFicha' | 'anotarGta' | 'condicaoDePagamento' | 'lancarGasto' | 'fecharCusto'
  // cadastro de produtores
  | 'criarProdutor' | 'editarProdutor'
  // comissões e pagamento efetivado (a quitação é «baixa»: a Consulta também lança)
  | 'comissao' | 'comissaoCliente' | 'quitacao'
  // 3F · revenda: preço de venda ao comprador final e o que ele pagou
  | 'precoDeVenda' | 'recebimento'
  // R2B · as baixas da 014 (comissão do cliente recebida; comissão do comprador paga fora do acerto)
  //       e a baixa do frete e do gasto (mora no documento da ficha)
  | 'recebimentoComissao' | 'pagamentoComissao' | 'pagarGasto'
  // extrato de viagem e acerto
  | 'despesa' | 'vale' | 'anularLancamento' | 'acerto' | 'pagarAcerto'
  // catira, notas
  | 'catira' | 'nota' | 'anularNota'
  // empresa e equipe
  | 'ferramentas' | 'dadosDaEmpresa' | 'equipe'
  // só no aparelho
  | 'padroesDoAparelho' | 'restaurarCopia' | 'adotarFichas'
  // o que se VÊ (o banco nem manda o dado a quem não vê)
  | 'verComissao' | 'verCatira' | 'verEquipe' | 'verVenda'

export interface LinhaDaMatriz {
  /** o que a ação faz, em português, para o teste e para quem lê */
  oque: string
  gestor: Regra
  comprador: Regra
  consulta: Regra
  /** a regra do banco que esta linha espelha (migração e política) */
  banco: string
  /**
   * R1A · gravação no documento da ficha: o comprador só faz com a ficha
   * ABERTA (encerrada, só reabrir). O gestor não tem essa trava (013 fichas_guarda).
   */
  travaEncerrada?: boolean
}

/** Ficha: a mesma regra para tudo o que se grava no documento dela. */
const FICHA_MUDAR = '001 fichas_mudar: gestor em todas; comprador só com autor_id = eu_pessoa(); consulta, nenhuma (gravo() exclui a consulta); 013 fichas_guarda: comprador em ficha encerrada só reabre'
/** a mesma linha, com a trava da ficha encerrada para o comprador */
const naFicha = (oque: string): LinhaDaMatriz => ({ oque, gestor: 'sim', comprador: 'seu', consulta: 'nao', banco: FICHA_MUDAR, travaEncerrada: true })
/** o que se faz DEPOIS de encerrar (GTA, pagamento combinado, gastos, custo): sem a trava — 013 fichas_guarda só trava pesos, lotes e porteiras */
const depoisDeEncerrar = (oque: string): LinhaDaMatriz => ({ oque, gestor: 'sim', comprador: 'seu', consulta: 'nao', banco: FICHA_MUDAR })

export const MATRIZ: Record<Acao, LinhaDaMatriz> = {
  criarFicha: { oque: 'abrir ficha nova («+ Nova pesagem»)', gestor: 'sim', comprador: 'sim', consulta: 'nao',
    banco: '001 fichas_criar: gravo() AND autor_id = eu_pessoa() — e o servidor responde 403 «A Consulta não grava fichas»' },
  editarOrigem: naFicha('mudar a origem (produtor, propriedade, GPS, data, finalidade)'),
  editarNegociacao: naFicha('mudar a negociação (forma, lotes, descontos, porteiras) e salvar versão nova'),
  pesar: naFicha('registrar, corrigir e excluir pesagem; criar lote na pesagem'),
  encerrar: naFicha('encerrar a ficha'),
  reabrir: { oque: 'reabrir a ficha (com motivo)', gestor: 'sim', comprador: 'seu', consulta: 'nao', banco: FICHA_MUDAR },
  cancelarFicha: naFicha('cancelar a ficha (com motivo)'),
  excluirFicha: naFicha('excluir ficha aberta sem pesagem'),
  anotarGta: depoisDeEncerrar('anotar o nº da GTA'),
  condicaoDePagamento: depoisDeEncerrar('combinar forma e parcelas (mora no documento da ficha)'),
  lancarGasto: depoisDeEncerrar('lançar e tirar gasto da ficha (frete, vacina)'),
  fecharCusto: depoisDeEncerrar('fechar e reabrir o custo'),

  criarProdutor: { oque: 'pôr produtor novo no cadastro', gestor: 'sim', comprador: 'sim', consulta: 'nao',
    banco: '001 produtores_criar: gravo() AND dono_id = eu_pessoa() — e o servidor responde 403 «A Consulta não grava produtores»' },
  editarProdutor: { oque: 'mudar ou tirar produtor do cadastro', gestor: 'sim', comprador: 'seu', consulta: 'nao',
    banco: '001 produtores_mudar: gestor em todos; comprador só com dono_id = eu_pessoa()' },

  comissao: { oque: 'lançar e corrigir a comissão a pagar (comprador)', gestor: 'sim', comprador: 'nao', consulta: 'nao',
    banco: '001 comissoes_criar/comissoes_mudar: meu_papel() = gestor' },
  comissaoCliente: { oque: 'lançar e corrigir a comissão a receber (cliente)', gestor: 'sim', comprador: 'nao', consulta: 'nao',
    banco: '010 comissoes_cliente_criar/_mudar: meu_papel() = gestor' },
  quitacao: { oque: 'lançar, anular e anexar comprovante de pagamento efetivado (baixa)', gestor: 'sim', comprador: 'nao', consulta: 'sim',
    banco: '013 quitacoes_criar/_mudar: meu_papel() IN (gestor, consulta); 013 comprovantes_criar de quitação: gestor e consulta' },

  precoDeVenda: { oque: 'lançar e corrigir o preço de venda da porteira Revenda (lote a lote)', gestor: 'sim', comprador: 'nao', consulta: 'nao',
    banco: '012 vendas_revenda_criar/_mudar: meu_papel() = gestor' },
  recebimento: { oque: 'lançar e anular o recebimento da venda (revenda), com comprovante (baixa)', gestor: 'sim', comprador: 'nao', consulta: 'sim',
    banco: '013 recebimentos_revenda_criar/_mudar: meu_papel() IN (gestor, consulta); 013 comprovantes_criar de recebimento: gestor e consulta' },
  recebimentoComissao: { oque: 'lançar e anular o que o cliente pagou da comissão, com comprovante (baixa)', gestor: 'sim', comprador: 'nao', consulta: 'sim',
    banco: '014 recebimentos_comissao_cliente_criar/_mudar: meu_papel() IN (gestor, consulta); 014 comprovantes_criar de recebimento_comissao: gestor e consulta' },
  pagamentoComissao: { oque: 'lançar e anular o pagamento da comissão do comprador fora do acerto, com comprovante (baixa)', gestor: 'sim', comprador: 'nao', consulta: 'sim',
    banco: '014 pagamentos_comissao_criar/_mudar: meu_papel() IN (gestor, consulta); 014 comprovantes_criar de pagamento_comissao: gestor e consulta' },
  pagarGasto: depoisDeEncerrar('marcar frete ou gasto como pago (pago em, favorecido, comprovante) e anular gasto com motivo — mora no documento da ficha'),

  despesa: { oque: 'lançar despesa de viagem', gestor: 'sim', comprador: 'seu', consulta: 'nao',
    banco: '005 lancamentos_extrato_criar: gestor para qualquer comprador; comprador só no próprio extrato e só despesa' },
  vale: { oque: 'lançar vale (adiantamento)', gestor: 'sim', comprador: 'nao', consulta: 'nao',
    banco: '005 lancamentos_extrato_criar: comprador só tipo = despesa' },
  anularLancamento: { oque: 'anular lançamento do extrato', gestor: 'sim', comprador: 'seu', consulta: 'nao',
    banco: '005 lancamentos_extrato_mudar: gestor em todos; comprador só o que ele lançou no próprio extrato' },
  acerto: { oque: 'marcar acertado e anular acerto', gestor: 'sim', comprador: 'nao', consulta: 'nao',
    banco: '005 acertos_criar/acertos_mudar: meu_papel() = gestor' },
  pagarAcerto: { oque: 'registrar o pagamento de um acerto já feito (valor, data, comprovante) — baixa', gestor: 'sim', comprador: 'nao', consulta: 'sim',
    banco: '013 registrar_pagamento_acerto: meu_papel() IN (gestor, consulta); comprovantes_criar do pagamento do acerto: gestor e consulta' },

  catira: { oque: 'lançar e anular movimento da catira (entrada da ficha, recria, morte, contagem)', gestor: 'sim', comprador: 'nao', consulta: 'nao',
    banco: '008 catira_criar/catira_mudar: meu_papel() = gestor' },
  nota: { oque: 'registrar nota fiscal', gestor: 'sim', comprador: 'seu', consulta: 'nao',
    banco: '009 notas_criar: gestor em qualquer ligação; comprador só na ficha dele ou numa despesa do extrato dele' },
  anularNota: { oque: 'anular nota fiscal', gestor: 'sim', comprador: 'seu', consulta: 'nao',
    banco: '009 notas_mudar: gestor em todas; comprador só a que ele registrou' },

  ferramentas: { oque: 'ligar e desligar ferramentas', gestor: 'sim', comprador: 'nao', consulta: 'nao',
    banco: '001 contas_mudar (coluna ferramentas da 011): gestor da própria empresa' },
  dadosDaEmpresa: { oque: 'mudar nome fantasia, CPF/CNPJ, telefone e cidade da empresa', gestor: 'sim', comprador: 'nao', consulta: 'nao',
    banco: '001 contas_mudar: gestor da própria empresa' },
  equipe: { oque: 'convidar pessoa, mudar papel, vínculo e desligar', gestor: 'sim', comprador: 'nao', consulta: 'nao',
    banco: '001 pessoas_criar e 004 pessoas_mudar: gestor da própria empresa' },

  padroesDoAparelho: { oque: 'categorias e padrões de ficha nova (só neste aparelho)', gestor: 'sim', comprador: 'sim', consulta: 'nao',
    banco: 'só no aparelho; servem para criar ficha — a mesma regra de 001 fichas_criar' },
  restaurarCopia: { oque: 'restaurar uma cópia de segurança (traz fichas para a fila)', gestor: 'sim', comprador: 'sim', consulta: 'nao',
    banco: 'o que entra vira ficha a enviar — a mesma regra de 001 fichas_criar' },
  adotarFichas: { oque: 'trazer para si as fichas presas neste aparelho de outra pessoa da empresa', gestor: 'sim', comprador: 'nao', consulta: 'nao',
    banco: 'sem regra nova: a ficha trazida sobe como do gestor por 001 fichas_criar (autor_id = eu_pessoa()), que ele já tem' },

  verComissao: { oque: 'ver comissões (a pagar e a receber)', gestor: 'sim', comprador: 'nao', consulta: 'sim',
    banco: '001 comissoes_ver e 010 comissoes_cliente_ver: vejo_tudo() (gestor e consulta)' },
  verCatira: { oque: 'ver a catira', gestor: 'sim', comprador: 'nao', consulta: 'sim',
    banco: '008 catira_ver: vejo_tudo()' },
  verEquipe: { oque: 'ver a equipe', gestor: 'sim', comprador: 'nao', consulta: 'sim',
    banco: '001 pessoas_ver: vejo_tudo() (o comprador vê só a si)' },
  verVenda: { oque: 'ver o preço de venda, os recebimentos e o ágio da revenda', gestor: 'sim', comprador: 'nao', consulta: 'sim',
    banco: '012 vendas_revenda_ver e recebimentos_revenda_ver: vejo_tudo() (gestor e consulta)' },
}

export const ACOES = Object.keys(MATRIZ) as Acao[]
export const PAPEIS: Papel[] = ['gestor', 'comprador', 'consulta']

export interface Contexto {
  /**
   * R1A · a ficha está encerrada? Para o comprador, só `reabrir` vale nela
   * (o banco recusa o resto: 013 fichas_guarda). Ausente = aberta.
   */
  encerrada?: boolean
  /**
   * A coisa é de quem está usando (ficha que ele criou, produtor que ele
   * cadastrou, lançamento que ele fez)? Ausente = sim: o comprador só RECEBE
   * do banco as fichas e os produtores dele (001 fichas_ver/produtores_ver),
   * então o que está no aparelho dele é dele. Quando o aparelho dele pode ter
   * coisa alheia (nota que o gestor registrou na ficha dele, lançamento que o
   * gestor fez no extrato dele), a tela diz `seu: false`.
   */
  seu?: boolean
}

/** A pergunta que toda tela faz antes de mostrar um botão de gravar. */
export function pode(papel: Papel | null | undefined, acao: Acao, contexto?: Contexto): boolean {
  if (!papel) return false
  const linha = MATRIZ[acao]
  if (!linha) return false
  const regra = linha[papel]
  if (regra === 'sim') return true
  if (regra === 'seu') return contexto?.seu !== false && !(linha.travaEncerrada && contexto?.encerrada)
  return false
}

/**
 * R1A · As «baixas» que a Consulta faz (decisão do gestor, 06/10/2026). Ela
 * continua sendo «somente consulta» para o resto: o selo fica.
 */
export const BAIXAS: Acao[] = ['quitacao', 'recebimento', 'pagarAcerto', 'recebimentoComissao', 'pagamentoComissao']

/** Este papel grava alguma coisa além das baixas? (a Consulta não) — para o selo «Somente consulta». */
export function somenteConsulta(papel: Papel | null | undefined): boolean {
  return !!papel && ACOES.every((a) => a.startsWith('ver') || BAIXAS.includes(a) || MATRIZ[a][papel] === 'nao')
}
